import { css } from '@emotion/react';

import type { ConnectionState, DriverStatus } from '@/lib/sync/types';
import { darkTheme } from '@/theme/dark-theme';
import { mq } from '@/theme/tokens';

export interface SyncStatusIndicatorProps {
  driftMs: number;
  connectionState: ConnectionState;
  driverStatus: DriverStatus;
}

const STATUS_COLORS = {
  green: darkTheme.success,
  yellow: darkTheme.warning,
  red: darkTheme.error,
  gray: darkTheme.textSecondary,
} as const;

function getStatusColor({ connectionState, driverStatus, driftMs }: SyncStatusIndicatorProps) {
  if (connectionState === 'disconnected' || connectionState === 'reconnecting') {
    return STATUS_COLORS.gray;
  }
  if (driverStatus === 'disconnected' || driverStatus === 'reconnected' || driftMs > 250) {
    return STATUS_COLORS.red;
  }
  if (driftMs >= 100) {
    return STATUS_COLORS.yellow;
  }
  return STATUS_COLORS.green;
}

function getStatusLabel({ connectionState, driverStatus, driftMs }: SyncStatusIndicatorProps) {
  if (connectionState === 'disconnected' || connectionState === 'reconnecting') {
    return 'Reconnecting...';
  }
  if (driverStatus === 'disconnected') {
    return 'Driver offline';
  }
  if (driverStatus === 'reconnected') {
    return 'Resyncing with driver...';
  }
  return `Drift: ${driftMs}ms`;
}

export function SyncStatusIndicator(props: SyncStatusIndicatorProps) {
  const color = getStatusColor(props);
  const label = getStatusLabel(props);

  return (
    <div
      role="status"
      aria-label="Sync status"
      title={label}
      css={css`
        position: fixed;
        bottom: 100px;
        right: 16px;
        z-index: 25;

        ${mq.md} {
          bottom: 60px;
        }
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background-color: ${color};
      `}
    />
  );
}
